// Central registry of every localStorage key that holds USER-GENERATED
// HISTORY in TipLingo. Used by ClearDataManager so the user can wipe
// each surface from one screen + by the global "Clear everything"
// button.
//
// Each entry has:
//   id          stable key for React + data-testid
//   label       short label shown in Settings
//   description what the user is about to clear
//   keys        localStorage keys that this surface writes
//   serverWipe  async function to also nuke server-side state (returns
//               null if there's no server-side artifact for this kind)
//
// Auth/preference keys are DELIBERATELY NOT listed here — clearing
// history must never sign the user out or reset their voice choice.

import api from "./api";
import { clearHistory } from "./historyLog";
import { clearCachedAudio } from "./phrasebook";

const SURFACES = [
  {
    id: "translate_history",
    label: "Translation history",
    description: "Recent translations shown by the clock icon.",
    keys: [],
    handler: () => clearHistory("translate"),
  },
  {
    id: "currency_history",
    label: "Currency conversions",
    description: "Recent currency conversions saved on this device.",
    keys: [],
    handler: () => clearHistory("currency"),
  },
  {
    id: "phrasebook_cache",
    label: "Translation cache",
    description: "Cached translations + audio used for offline replay.",
    keys: ["voyago_translate_cache_v2"],
    handler: () => {
      clearCachedAudio();
      try { localStorage.removeItem("voyago_translate_cache_v2"); } catch { /* no-op */ }
    },
  },
  {
    id: "phrasebook_favorites",
    label: "Phrasebook favorites",
    description: "Phrases you saved with the bookmark icon.",
    keys: ["voyago_favorites_v1"],
    handler: () => {
      try { localStorage.removeItem("voyago_favorites_v1"); } catch { /* no-op */ }
    },
  },
  {
    id: "custom_phrases",
    label: "Custom phrases",
    description: "Phrases you added to the phrasebook yourself.",
    keys: ["tiplingo_custom_phrases_v1"],
    handler: () => {
      try { localStorage.removeItem("tiplingo_custom_phrases_v1"); } catch { /* no-op */ }
    },
  },
  {
    id: "saved_tips",
    label: "Saved tips",
    description: "Every tip you saved to the Saved tab. Cloud copies are removed when signed in.",
    keys: [],
    handler: async () => {
      try {
        const r = await api.delete("/tips/all");
        return { serverDeleted: r?.data?.deleted || 0 };
      } catch {
        // Anonymous (401) — nothing to delete server-side; local list
        // lives in the SavedTab component's React state and disappears
        // on next render via the cache invalidation below.
        return { serverDeleted: 0 };
      }
    },
  },
  {
    id: "lost_luggage",
    label: "Lost luggage claims",
    description: "Anonymous draft claims saved on this device.",
    keys: ["tiplingo_lost_luggage_drafts_v1"],
    handler: async () => {
      try { localStorage.removeItem("tiplingo_lost_luggage_drafts_v1"); } catch { /* no-op */ }
      // Lost-luggage delete still uses the legacy ?user_id= query param
      // pattern. Resolve the current user via /auth/me first; if not
      // signed in, skip the server call (local drafts already wiped).
      try {
        const me = await api.get("/auth/me");
        const uid = me?.data?.user_id;
        if (uid) {
          await api.delete(`/lost-luggage/claims/all?user_id=${encodeURIComponent(uid)}`);
        }
      } catch { /* anon — non-fatal */ }
    },
  },
];

export function listClearableSurfaces() {
  return SURFACES.map((s) => ({
    id: s.id,
    label: s.label,
    description: s.description,
  }));
}

export async function clearSurface(id) {
  const surface = SURFACES.find((s) => s.id === id);
  if (!surface) return false;
  try {
    const result = surface.handler();
    if (result && typeof result.then === "function") {
      await result;
    }
    return true;
  } catch {
    return false;
  }
}

export async function clearAllSurfaces() {
  const errors = [];
  for (const surface of SURFACES) {
    try {
      const result = surface.handler();
      if (result && typeof result.then === "function") {
        await result;
      }
    } catch (e) {
      errors.push(surface.id);
    }
  }
  return { ok: errors.length === 0, errors };
}
